export type AppRuntimeConfig = {
  BACKEND_URL: string;
  APP_NAME: string;
  LOGIN_PROVIDER: string;
  CONFIG_TYPE: string;
  AZURE_CLIENT_ID: string;
  AZURE_TENANT_ID: string;
  AZURE_REDIRECT_URL: string;
  AZURE_SCOPE: string;
  KEYCLOAK_AUTH_SERVER_URL: string;
  KEYCLOAK_AUTH_REALM: string;
  KEYCLOAK_AUTH_CLIENT_ID: string;
};

type RuntimeConfigSource = Partial<AppRuntimeConfig> & {
  CLIENT_ID?: string;
  TENANT_ID?: string;
  REDIRECT_URL?: string;
  SCOPE?: string;
};

declare global {
  interface Window {
    // @ts-ignore
    APP_CONFIG?: RuntimeConfigSource;
    // @ts-ignore
    appConfig?: RuntimeConfigSource;
    LOCAL_APP_CONFIG?: RuntimeConfigSource;
    localAppConfig?: RuntimeConfigSource;
  }
}

const DEFAULT_RUNTIME_CONFIG: AppRuntimeConfig = {
  BACKEND_URL: '/api',
  APP_NAME: '',
  LOGIN_PROVIDER: 'azure',
  CONFIG_TYPE: 'runtime',
  AZURE_CLIENT_ID: '',
  AZURE_TENANT_ID: '',
  AZURE_REDIRECT_URL: '',
  AZURE_SCOPE: '',
  KEYCLOAK_AUTH_SERVER_URL: '',
  KEYCLOAK_AUTH_REALM: '',
  KEYCLOAK_AUTH_CLIENT_ID: '',
};

const toString = (value: unknown, fallback = ''): string => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
};

const getConfigSource = (): RuntimeConfigSource => {
  if (typeof window === 'undefined') {
    return {};
  }

  const legacyConfig = window.appConfig ?? {};
  const runtimeConfig = window.APP_CONFIG ?? {};
  const localRuntimeConfig =
    process.env.NODE_ENV === 'development'
      ? (window.LOCAL_APP_CONFIG ?? window.localAppConfig ?? {})
      : {};

  return {
    ...legacyConfig,
    ...runtimeConfig,
    ...localRuntimeConfig,
  };
};

export const getRuntimeConfig = (): AppRuntimeConfig => {
  const source = getConfigSource();

  return {
    BACKEND_URL: toString(source.BACKEND_URL, DEFAULT_RUNTIME_CONFIG.BACKEND_URL),
    APP_NAME: toString(source.APP_NAME, DEFAULT_RUNTIME_CONFIG.APP_NAME),
    LOGIN_PROVIDER: toString(source.LOGIN_PROVIDER, DEFAULT_RUNTIME_CONFIG.LOGIN_PROVIDER).toLowerCase(),
    CONFIG_TYPE: toString(source.CONFIG_TYPE, DEFAULT_RUNTIME_CONFIG.CONFIG_TYPE),
    AZURE_CLIENT_ID: toString(source.AZURE_CLIENT_ID ?? source.CLIENT_ID, DEFAULT_RUNTIME_CONFIG.AZURE_CLIENT_ID),
    AZURE_TENANT_ID: toString(source.AZURE_TENANT_ID ?? source.TENANT_ID, DEFAULT_RUNTIME_CONFIG.AZURE_TENANT_ID),
    AZURE_REDIRECT_URL: toString(source.AZURE_REDIRECT_URL ?? source.REDIRECT_URL, DEFAULT_RUNTIME_CONFIG.AZURE_REDIRECT_URL),
    AZURE_SCOPE: toString(source.AZURE_SCOPE ?? source.SCOPE, DEFAULT_RUNTIME_CONFIG.AZURE_SCOPE),
    KEYCLOAK_AUTH_SERVER_URL: toString(source.KEYCLOAK_AUTH_SERVER_URL, DEFAULT_RUNTIME_CONFIG.KEYCLOAK_AUTH_SERVER_URL),
    KEYCLOAK_AUTH_REALM: toString(source.KEYCLOAK_AUTH_REALM, DEFAULT_RUNTIME_CONFIG.KEYCLOAK_AUTH_REALM),
    KEYCLOAK_AUTH_CLIENT_ID: toString(source.KEYCLOAK_AUTH_CLIENT_ID, DEFAULT_RUNTIME_CONFIG.KEYCLOAK_AUTH_CLIENT_ID),
  };
};

export const hasAzureAuthConfig = (): boolean => {
  const config = getRuntimeConfig();
  return config.AZURE_CLIENT_ID.length > 0 && config.AZURE_TENANT_ID.length > 0;
};

export const hasKeycloakAuthConfig = (): boolean => {
  const config = getRuntimeConfig();
  return (
    config.KEYCLOAK_AUTH_SERVER_URL.length > 0 &&
    config.KEYCLOAK_AUTH_REALM.length > 0 &&
    config.KEYCLOAK_AUTH_CLIENT_ID.length > 0
  );
};
